import { createFeatureSelector, createSelector } from '@ngrx/store';
import { IMasterDataState } from './masterdata.reducer';
import { ContractStatus } from '../../../models';
import { getPersonalData } from '../user-profile/user-profile.selectors';

const selectFeature = createFeatureSelector<IMasterDataState>('masterData');

export const getUser = createSelector(selectFeature, state => state.user);
export const getUserActionState = createSelector(selectFeature, state => state.userActionState);

export const getContracts = createSelector(selectFeature, state => state.contracts);
export const getNonExpiredContracts = createSelector(getContracts, contracts =>
  contracts.filter(contract => contract.status !== ContractStatus.EXPIRED)
);
export const getContractsActionState = createSelector(
  selectFeature,
  state => state.contractsActionState
);

export const getSelectedContract = createSelector(selectFeature, state => state.selectedContract);

/**
 * The contract that ticket-overview queries should be scoped to.
 *
 * ART-19612: a resident whose contracts are ALL expired but who still has an
 * active cooperative membership is kept in a read-only "preview" mode (see
 * CustomerService.onlyExpiredContractsAndActiveMembership). getPrimaryContract
 * still auto-selects one of the expired contracts, which would otherwise make
 * the ticket overview show that contract's tickets. For this cohort we return
 * `null` so the ticket effects fall back to the same empty/contractless filter
 * a user with no contract already uses (`contract?.id ? [contract.id] : []`),
 * i.e. the resident only sees tickets that are not tied to a contract.
 *
 * NOTE: this relies on the backend returning contractless tickets (not "all")
 * for an empty `contractNumbers` filter — the established behaviour for
 * no-contract users. The unread-message badge is a separate, aggregate count
 * and is intentionally NOT handled here.
 */
export const getSelectedContractForTicketsOverview = createSelector(
  getSelectedContract,
  getContracts,
  getPersonalData,
  (selectedContract, contracts, personalData) => {
    const allContractsExpired =
      !!contracts?.length &&
      contracts.every(contract => contract.status === ContractStatus.EXPIRED);
    const hasActiveMembership = !!personalData?.cooperativeData?.activeMembership;

    return allContractsExpired && hasActiveMembership ? null : selectedContract;
  }
);

export const getUpdateMasterDataActionState = createSelector(
  selectFeature,
  state => state.updateMasterDataActionState
);

export const getSelectedContractDocuments = createSelector(
  selectFeature,
  state => state.selectedContractDocuments
);

export const getSelectedContractDocumentsActionState = createSelector(
  selectFeature,
  state => state.selectedContractDocumentsActionState
);

export const getSelectedContractSelectedDocument = createSelector(
  selectFeature,
  state => state.selectedContractSelectedDocument
);

export const getContractsCount = createSelector(getContracts, contracts => contracts.length);

export const getMasterDataChangeConfiguration = createSelector(
  selectFeature,
  state => state.masterDataChangeConfiguration
);

export const getMasterDataChangeConfigurationActionState = createSelector(
  selectFeature,
  state => state.masterDataChangeConfigurationActionState
);

export const getMasterDataChange = createSelector(selectFeature, state => state.masterDataChange);
export const getMasterDataChangeActionState = createSelector(
  selectFeature,
  state => state.masterDataChangeActionState
);

export const getSendMasterDataChangeRequestActionState = createSelector(
  selectFeature,
  state => state.sendMasterdataChangeRequestActionState
);

export const getSendMasterDataChangeRequestResponse = createSelector(
  selectFeature,
  state => state.sendMasterDataChangeRequestResponse
);

export const getSendMasterDataChangeTicketActionState = createSelector(
  selectFeature,
  state => state.sendMasterdataChangeTicketActionState
);

export const getSendMasterDataChangeTicketResponse = createSelector(
  selectFeature,
  state => state.sendMasterDataChangeTicketResponse
);
